import _ from 'lodash';
import Swal from 'sweetalert2';
import {
  V3_ADD_TO_CART,
  V3_DECLINE_INTERSTITIAL_PRODUCT,
  V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED,
} from './v3Order';

import cartUtils from '../../utils/cart';
import { personalizationSetToSameValueSelector } from '../../selectors';
import {
  isShippingRestrictedForParent,
  availableIncentiveProducts,
} from '../../components/v3/helpers/internationalRestriction';

const childRequiresShipping = (child) => child?.requires_shipping === true;

const selectedProductsFromProductConfig = (productConfig) =>
  Object.values(productConfig || {}).flatMap((config) => {
    const selected = [];
    if (config?.product) selected.push(config.product);
    if (Array.isArray(config?.products)) selected.push(...config.products);
    return selected;
  });

const blockInternationalShippingChild = ({
  productData,
  productConfig,
  blockInternationalShipping,
}) => {
  if (!blockInternationalShipping) return false;

  const children = Array.isArray(productData?.child_products)
    ? productData.child_products
    : [];
  if (children.length === 0) return false;

  const isParent =
    productData?.product_type === 'parent_product' || productData?.is_ypx;

  if (productData?.is_multi_config === true) {
    return children.some(childRequiresShipping);
  }

  const selectedProducts = selectedProductsFromProductConfig(productConfig);
  if (isParent && selectedProducts.length > 0) {
    return selectedProducts.some(childRequiresShipping);
  }

  if (isParent) {
    return children.every(childRequiresShipping);
  }

  return children.some(childRequiresShipping);
};

export const addToCart =
  ({
    studentId,
    fullproductData,
    backgroundIds,
    backgroundSelections,
    poseIds,
    poses,
    isInterstitialProduct = false,
    quantity = 1,
    personalizationValue = null,
    redirectSource = null,
    requiredFields,
    legacyRequiredFields,
    productOptionKey,
    productOptions,
    selectedCropOptions = [],
    childCropSelections = [],
  }) =>
  async (dispatch, getState) => {
    const {
      galleryId,
      incentiveProducts,
      isPrepay,
      isPostShootOrder,
      personalizationProduct,
      postpayRequiresSameValuePersonalization,
      prepayRequiresSameValuePersonalization,
      shoot,
    } = getState().shoot;

    // Ensure isPostEvent and gallery_id are set correctly for ClassAct and other gallery orders
    const additionalData = {
      ...getState().additionalData,
      isPostEvent:
        isPostShootOrder || getState().additionalData?.isPostEvent || false,
      gallery_id: galleryId || getState().additionalData?.gallery_id || null,
    };
    const { orders, productConfig } = getState().v3Order;
    const { selectedStudent } = getState().additionalData;
    const restricted = isShippingRestrictedForParent(shoot, getState().parent);
    const availableIncentives = availableIncentiveProducts(
      incentiveProducts,
      restricted,
    );
    const incentiveProductIds = availableIncentives.map(
      (product) => product.id,
    );

    const orderKey = `${shoot?.event_id || shoot?.id}-${selectedStudent?.id}`;
    const currentOrder = orders[orderKey];
    const cartItems = currentOrder?.cartItems || [];

    const cartItemsIncludeRetouching =
      cartUtils.cartItemsIncludeRetouching(cartItems);
    const cartItemsContainRetouchingProduct =
      cartUtils.cartItemsContainRetouchingProduct(cartItems);

    const formattedRequiredFields = [];
    let requiredProductFields = fullproductData.required_product_fields;
    const childRequiredFields = (fullproductData.child_products || [])
      .map((child) => child.required_product_fields)
      .flat()
      .filter((el) => el !== null);

    requiredProductFields = (requiredProductFields || [])
      .concat(childRequiredFields)
      .filter((el) => el !== undefined && el !== null);

    const legacyRequiredProductFields =
      fullproductData.legacy_required_product_fields;

    if (Array.isArray(requiredProductFields)) {
      requiredProductFields.forEach((field) => {
        const productField = {
          id: field.id,
          key: field.key,
          name: field.name,
          label: field.label,
          value: requiredFields[field.key],
        };
        formattedRequiredFields.push(productField);
      });
    }

    const formattedLegacyRequiredFields = (
      legacyRequiredProductFields || []
    ).reduce((acc, field) => {
      acc[field.name] = legacyRequiredFields[field.name];
      return acc;
    }, {});

    // When adding the retouching product, ensure it's not in the cart first
    if (fullproductData.is_retouching) {
      if (cartItemsIncludeRetouching || cartItemsContainRetouchingProduct) {
        const message = cartItemsIncludeRetouching
          ? 'One of the items in the cart already includes retouching :)'
          : 'You already have retouching added to your order :)';

        return Swal.fire({
          title: 'Retouching already added',
          text: message,
          icon: 'info',
          // showCancelButton: false,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'OK',
        });
      }
    }

    // TODO: don't allow users to add personalization more than once
    // TODO: don't allow users to add retouching to an empty cart
    // TODO: don't allow users to add personalization to an empty cart

    // TODO: describe what this does
    const personalizationSetToSameValue = personalizationSetToSameValueSelector(
      getState(),
    );

    // TODO: extract this to a utility function (same as above)
    // Specify the keys we want to store in the cart
    const productData = _.pick(fullproductData, [
      'additional_data',
      'background_options_count',
      'contents',
      'id',
      'image_thumbnail',
      'image_type',
      'incentive_threshold',
      'includes_retouching',
      'is_byo',
      'is_retouching',
      'is_personalization',
      'is_ypx',
      'is_multi_config',
      'lock_group',
      'lock_type',
      'personalization_status',
      'poses_count',
      'price',
      'product_type',
      'name',
      'nth_free_amount_allowed',
      'nth_free_product_id',
      'nth_free_sku',
      'nth_free_threshold',
      'nth_free_value',
      'is_pre_cart_offer',
      'requires_shipping',
      'sku',
      'child_products',
      'required_product_fields',
      'product_options',
      'product_option_sets',
    ]);

    const isPreCartOfferProduct = productData.is_pre_cart_offer;

    let personalizationValueForCart = null;

    // First look through productConfig, which is what is submitted for YPX and BYO products
    // This case handles orders where the personalization value is set to the same value for all products
    // TODO: handle cases when YPX/BYO products can have different personalization values
    if (
      personalizationSetToSameValue &&
      productConfig &&
      productConfig.length > 0
    ) {
      let personalizationFromProductConfig = null;

      productConfig.forEach((config) => {
        if (config.personalization) {
          personalizationFromProductConfig = config.personalization;
        }
      });

      if (
        personalizationFromProductConfig !== null &&
        personalizationFromProductConfig !== ''
      ) {
        personalizationValueForCart = personalizationFromProductConfig;
      }
    }

    // Look at personalizationValue, which is set on the submitted item
    if (
      personalizationValue &&
      personalizationValue !== '' &&
      personalizationValue !== null
    ) {
      personalizationValueForCart = personalizationValue;
    }

    // We need to use the event_id if it exists, otherwise use the shoot id
    // DP2 shoot ids are different from the shoot (event) id
    const eventId = shoot?.event_id || shoot?.id;

    const cartObject = cartUtils.configureCartItem({
      backgroundIds,
      backgroundSelections,
      incentiveProductIds,
      productConfig,
      productData,
      poses,
      poseIds,
      personalizationValueForCart,
      personalizationSetToSameValue,
      quantity,
      requiredFields: formattedRequiredFields,
      legacyRequiredFields: formattedLegacyRequiredFields,
      productOptionKey,
      productOptions,
      eventId,
      selectedCropOptions,
      childCropSelections,
      blockInternationalShipping: shoot?.block_international_shipping === true,
      blockInternationalShippingChild: blockInternationalShippingChild({
        productData,
        productConfig,
        blockInternationalShipping:
          shoot?.block_international_shipping === true,
      }),
    });

    // TODO: revisit this logic -- it's a bit confusing to
    // call 'decline' when the product is being added to the cart...
    // we should probably create an action to support this case,
    // something like V3_MARK_INTERSTITIAL_PRODUCT_AS_PROMPTED, etc.
    if (isInterstitialProduct) {
      dispatch({
        type: V3_DECLINE_INTERSTITIAL_PRODUCT,
        payload: { orderKey, productId: cartObject.productId },
      });
    }

    let payload = {
      additionalData,
      cartObject,
      isPrepay,
      personalizationSetToSameValue,
      selectedStudent,
      shoot,
      redirectSource,
      requiredFields: formattedRequiredFields,
    };

    const cartObjectChildPersonalizationValues = (cartObject.children || [])
      .map((child) => child.personalizationValue)
      .filter((value) => value !== undefined && value !== null && value !== '');

    // If the cart object or its child products are personalized, send the personalization product (if it exists)
    if (
      personalizationProduct &&
      (cartObject.personalizationValue ||
        cartObjectChildPersonalizationValues.length > 0)
    ) {
      // TODO: extract this to a utility function (same as above)
      const personalizationProductData = _.pick(personalizationProduct, [
        'additional_data',
        'background_options_count',
        'contents',
        'id',
        'image_thumbnail',
        'image_type',
        'incentive_threshold',
        'includes_retouching',
        'is_byo',
        'is_retouching',
        'is_personalization',
        'is_ypx',
        'is_multi_config',
        'lock_group',
        'lock_type',
        'personalization_status',
        'poses_count',
        'price',
        'product_type',
        'name',
        'nth_free_amount_allowed',
        'nth_free_product_id',
        'nth_free_sku',
        'nth_free_threshold',
        'nth_free_value',
        'requires_shipping',
        'sku',
        'child_products',
        'required_product_fields',
        'product_options',
      ]);

      const configuredPersonalizationProduct = cartUtils.configureCartItem({
        backgroundIds: [],
        backgroundSelections: [],
        incentiveProductIds,
        productConfig,
        productData: personalizationProductData,
        poses: [],
        poseIds: [],
        personalizationValueForCart,
        personalizationSetToSameValue,
        quantity,
        requiredFields: formattedRequiredFields,
        legacyRequiredFields: formattedLegacyRequiredFields,
        productOptionKey: null, // TODO: confirm null works here
        productOptions: {},
        eventId,
        blockInternationalShipping:
          shoot?.block_international_shipping === true,
        blockInternationalShippingChild: blockInternationalShippingChild({
          productData: personalizationProductData,
          productConfig,
          blockInternationalShipping:
            shoot?.block_international_shipping === true,
        }),
      });

      payload = {
        ...payload,
        personalizationProduct: configuredPersonalizationProduct,
      };
    }

    if (isPreCartOfferProduct) {
      dispatch({
        type: V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED,
        payload: { orderKey, productId: cartObject.productId },
      });
    }

    dispatch({
      type: V3_ADD_TO_CART,
      payload,
    });
  };
